import {
  FormContainer,
  ISelectCountry,
  ISelectType,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useTranslation } from "react-i18next";
import {
  createDonor,
  getDonorById,
  updateDonorById,
} from "../../../../services/donors.service";
import { donorsSchema, TDonorsForm } from "./donorsSchema";
import { DonorsFields } from "./DonorsFields";
import { useCallback, useEffect, useState } from "react";
import { countriesList } from "../../../../services/country.service";
import ExternalService from "../../../../services/external.service";
import { IDonor, IUpdateDonor } from "../../../../models/donor";

const translationPath = "page.4uDonation.donors.";

const cepService = new ExternalService();

export const Donors = () => {
  const { t } = useTranslation();

  const { sendRequest: save, loading: sLoading } = useFetch(createDonor);
  const { sendRequest: update, loading: uLoading } = useFetch(updateDonorById);
  const { sendRequest: get, loading: gLoading } = useFetch(getDonorById);
  const { sendRequest: getCountries, loading: cLoading } =
    useFetch(countriesList);

  const [countries, setCountries] = useState<ISelectCountry[]>([]);

  useEffect(() => {
    const fetchCountries = async () => {
      const { data } = await getCountries(null);
      if (data) {
        const sorted = data.sort(
          (a, b) => (b.ddiCode?.length ?? 0) - (a.ddiCode?.length ?? 0)
        );
        setCountries(sorted);
      }
    };
    fetchCountries();
  }, [getCountries]);

  const handleSave = useCallback(
    (v: TDonorsForm): Promise<any> => {
      const payload: IDonor = {
        ...v,
        userIntegrationTypeId: v.userIntegrationType?.id,
        donorTypeId: v.donorType?.id,
        genderId: v.gender?.id,
        birthDate: v.birthDateField
          ? v.birthDateField.toISOString().split("T")[0]
          : undefined,
        individualId: v.individual ? v.individual.id : undefined,
        legalEntityId: v.legalEntity ? v.legalEntity.id : undefined,
        attendantId: v.attendant ? v.attendant.id : undefined,
        attendantSecondaryId: v.attendantSecondary
          ? v.attendantSecondary.id
          : undefined,
        addressess:
          v.addressess &&
          v.addressess.map((address) => ({
            ...address,
            cityId: address.city,
            donorAddressTypeId: address.addressType,
            complement: address.complement || "",
            neighborhoodName: address.otherNeighborhood
              ? address.neighborhoodName
              : address.neighborhood?.name,
            zipCode: address.zipCode?.replace("-", ""),
          })),
        contacts: v.contactsObject
          ? v.contactsObject.map((contact) => {
              const { ddiCode, value, ...rest } = contact;
              const cleanedValue = value ? value.replace(/\D/g, "") : "";
              const finalValue = ddiCode
                ? `${ddiCode.ddiCode}${cleanedValue}`
                : cleanedValue;
              return {
                donorContactTypeId: contact.donorContactType
                  ? contact.donorContactType.id
                  : "",
                value: finalValue,
              };
            })
          : [],
      };

      return save(payload);
    },
    [save]
  );

  const handleUpdate = useCallback(
    (v: TDonorsForm, id: string): Promise<any> => {
      const payload: IUpdateDonor = {
        ...v,
        id,
        userIntegrationTypeId: v.userIntegrationType?.id,
        donorTypeId: v.donorType?.id,
        genderId: v.gender?.id,
        birthDate: v.birthDateField
          ? v.birthDateField.toISOString().split("T")[0]
          : undefined,
        individualId: v.individual ? v.individual.id : undefined,
        legalEntityId: v.legalEntity ? v.legalEntity.id : undefined,
        attendantId: v.attendant ? v.attendant.id : undefined,
        attendantSecondaryId: v.attendantSecondary
          ? v.attendantSecondary.id
          : undefined,
        addressess:
          v.addressess &&
          v.addressess.map((address) => ({
            ...address,
            cityId: address.city,
            donorAddressTypeId: address.addressType,
            complement: address.complement || "",
            neighborhoodName: address.otherNeighborhood
              ? address.neighborhoodName
              : address.neighborhood?.name,
            zipCode: address.zipCode?.replace("-", ""),
          })),
        contacts:
          v.contactsObject && v.contactsObject.length > 0
            ? v.contactsObject.map((contact) => {
                const { ddiCode, value, ...rest } = contact;
                const cleanedValue = value ? value.replace(/\D/g, "") : "";
                const finalValue = ddiCode?.ddiCode
                  ? `${ddiCode.ddiCode}${cleanedValue}`
                  : cleanedValue;

                return {
                  id: rest.id!,
                  donorContactTypeId: rest.donorContactType
                    ? rest.donorContactType.id
                    : "",
                  value: finalValue,
                };
              })
            : [],
      };

      return update({ item: payload, id });
    },
    [update]
  );

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TDonorsForm>> => {
      const { data, success } = await get(id);

      if (success && data) {
        const transformedAddressess = data.addresses
          ? await Promise.all(
              data.addresses.map(async (address) => {
                const complement = address.complement || "";
                const city = address.city;
                const addressType = address.donorAddressType;
                const provincy = address.city?.provincy || null;
                const otherNeighborhood = address.neighborhoodName
                  ? false
                  : true;
                const neighborhoodName = address.neighborhoodName || "";
                const zipCode = address.zipCode
                  ? address.zipCode.replace(/\D/g, "")
                  : "";
                const number = address.number || "";
                const addressDescription = address.addressDescription || "";
                let neighborhood;

                if (address.zipCode) {
                  try {
                    const zip = address.zipCode.replace(/\D/g, "");
                    if (zip.length === 8) {
                      const response = await cepService.getCep(zip);
                      if (response.status === 200 && response.data) {
                        neighborhood = response?.data?.neighborhood ?? null;
                      }
                    }
                  } catch (e) {
                    console.error("Failed to fetch CEP data on load", e);
                  }
                }

                return {
                  complement,
                  city,
                  addressType,
                  provincy,
                  otherNeighborhood,
                  neighborhoodName,
                  zipCode,
                  number,
                  addressDescription,
                  neighborhood,
                };
              })
            )
          : [];

        const transformedData: TDonorsForm = {
          ...data,
          donorType: data.donorType
            ? ({
                id: data.donorType.id,
                name: data.donorType.name,
                code: data.donorType.code,
              } as ISelectType)
            : undefined,
          donorTypeId: data.donorType?.id,
          userIntegrationType:
            data.attendant?.userIntegration?.userIntegrationType,
          userIntegrationTypeId:
            data.attendant?.userIntegration?.userIntegrationType.id,
          attendant: data.attendant
            ? { id: data.attendant.id, name: data.attendant.fullName }
            : undefined,
          attendantSecondary: data.attendantSecondary
            ? {
                id: data.attendantSecondary.id,
                name: data.attendantSecondary.fullName,
              }
            : undefined,
          addressess: transformedAddressess,
          individual: data.individual
            ? {
                id: data.individual.id,
                individualName: data.individual.name,
                document: data.individual.code,
              }
            : null,
          legalEntity: data.legalEntity
            ? {
                id: data.legalEntity?.id,
                corporateName: data.legalEntity?.name,
                document: data.legalEntity?.code,
              }
            : null,
          contacts: [],
          contactsObject:
            data.contacts?.map((contact) => {
              if (!contact.value || countries.length === 0) {
                return {
                  value: contact.value ?? "",
                  donorContactType: contact.donorContactType,
                  ddiCode: null,
                };
              }

              const country = countries.find(
                (c) => c.ddiCode && contact.value.startsWith(c.ddiCode)
              );
              const number = country
                ? contact.value.slice(country.ddiCode.length)
                : contact.value;

              return {
                donorContactType: contact.donorContactType,
                ddiCode: country || null,
                value: number,
              };
            }) || [],

          genderId: data.gender?.id,
          birthDateField: data.birthDate
            ? new Date(data.birthDate.replace(/-/g, "/"))
            : undefined,
          birthDate: data.birthDate || "",
          attendantId: data.attendant?.id,
          attendantSecondaryId: data.attendantSecondary?.id,
          individualId: data.individual?.id,
          legalEntityId: data.legalEntity?.id,
          integrationId: data.integrationId,
          name: data.name,
          document: data.document || "",
        };
        return { data: transformedData, success };
      }
      return { data: null, success: false };
    },
    [get, countries, cepService]
  );
  const loading = sLoading || uLoading || gLoading || cLoading;

  return (
    <FormContainer<TDonorsForm>
      saveFunction={handleSave}
      updateFunction={handleUpdate}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "title")}
      schema={donorsSchema}
      subtitle={t(translationPath + "new_donor")}
      subtitleWatchField={"name"}
      defaultValues={{
        contacts: [],
        addressess: [],
      }}
      fields={() => <DonorsFields />}
    />
  );
};
